import Vue from 'vue';
import VueRouter from 'vue-router';
import { routesNames } from '../enums/routesNames';
import permissionCheckService from '../services/permissionCheckService';
import { AuthGetterTypes } from '../store/getters';
import vueAppSubfolder from '../utils/appSubfolderUtils';

Vue.use(VueRouter);

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
	return originalPush.call(this, location).catch((err) => {
		if (err.name !== 'NavigationDuplicated') throw err;
	});
};

const ifNotAuthenticated = (to, from, next) => {
	let isAuthenticated = localStorage.bearer;
	if (!isAuthenticated) {
		next();
	} else {
		next({ path: '/' });
	}
};

const ifAuthenticated = (to, from, next) => {
	let isAuthenticated = localStorage.bearer;
	if (
		to.name !== routesNames.LOGIN &&
		from.name !== routesNames.LOGIN &&
		!isAuthenticated
	) {
		next({ name: routesNames.LOGIN });
	} else {
		next();
	}
};

const router = new VueRouter({
	mode: 'history',
	base: vueAppSubfolder.get(),
	routes: [
		{
			path: '/login',
			name: routesNames.LOGIN,
			component: () => import('@/components/Login'),
			beforeEnter: ifNotAuthenticated,
		},
		{
			path: '/restricted-access',
			name: routesNames.RESTRICTED_ACCESS,
			component: () => import('@/components/shared/RestrictedAccess'),
		},
		{
			path: '/',
			redirect: '/homepage',
			component: () => import('@/components/layout/LayoutIndex'),
			beforeEnter: ifAuthenticated,
			children: [
				{
					name: 'DevFixes',
					path: '/dev-fixes',
					component: () => import('@/components/DevFixes'),
				},
				{
					name: routesNames.HOMEPAGE,
					path: '/homepage',
					component: () => import('@/components/Homepage'),
				},
				{
					name: routesNames.MY_PROFILE,
					path: '/myprofile',
					component: () => import('@/components/myProfile/MyProfile'),
				},
				{
					name: routesNames.ACTIVITY,
					path: '/activity',
					component: () => import('@/components/activities/Activity'),
					children: [
						{
							name: routesNames.ACTIVITY_GRID,
							path: ':activityId',
							component: () =>
								import('@/components/activities/ActivityGrid'),
						},
					],
				},
				{
					name: routesNames.DATASETS,
					path: '/grids',
					component: () => import('@/components/grids/Grid'),
					children: [
						{
							name: routesNames.DATASETS_GRIDS,
							path: ':pageName',
							props: {
								type: 'grid',
							},
							component: () =>
								import('@/components/grids/grid/GridTable'),
						},
						{
							name: routesNames.DATASETS_HISTORY,
							path: ':pageName/history',
							props: {
								type: 'history',
							},
							component: () =>
								import(
									'@/components/grids/history/GridHistory'
								),
						},
						{
							name: routesNames.DATASETS_RECORD_HISTORY,
							path: ':pageName/history/:assetId',
							props: {
								type: 'history',
							},
							component: () =>
								import(
									'@/components/grids/history/GridRecordHistory'
								),
						},

						{
							name: routesNames.DATASETS_EDIT,
							path: ':pageName/edit/:recordId',
							props: {
								type: 'edit',
							},
							component: () =>
								import(
									'@/components/grids/edit/ManageGridView'
								),
						},
						{
							name: routesNames.DATASETS_ADD,
							path: ':pageName/add',
							props: {
								type: 'add',
							},
							component: () =>
								import(
									'@/components/grids/edit/ManageGridView'
								),
						},
					],
				},
				{
					name: routesNames.INVENTORY,
					path: '/inventory',
					component: () => import('@/components/inventory/Inventory'),

					children: [
						{
							name: routesNames.INVENTORY_GRID,
							path: ':formId/grid',
							props: {
								type: 'grid',
							},
							component: () =>
								import(
									'@/components/inventory/grid/InventoryGrid'
								),
						},
						{
							name: routesNames.INVENTORY_FORM,
							path: ':formId/form',
							props: {
								type: 'form',
							},
							component: () =>
								import('@/components/inventory/form/Form'),
						},
						{
							name: routesNames.INVENTORY_FORM_HISTORY,
							path: ':formId/history/:recordId',
							props: {
								type: 'history',
							},
							component: () =>
								import(
									'@/components/inventory/history/InventoryHistory'
								),
						},
					],
				},

				{
					name: routesNames.REPORTS,
					path: '/reports',
					component: () => import('@/components/reports/Reports'),
					children: [
						{
							name: routesNames.REPORT_WORKBOOK,
							path: 'workbook/:workbookId',
							component: () =>
								import(
									'@/components/reports/Workbooks/Workbook'
								),
						},
						{
							name: routesNames.REPORT_GRID_CUSTOM,
							path: ':reportId',
							component: () =>
								import(
									'@/components/reports/Grid/ReportGridCustom'
								),
						},
						{
							name: routesNames.REPORT_GRID,
							path: ':reportId/gridView/:gridViewId',
							component: () =>
								import('@/components/reports/Grid/ReportGrid'),
						},
					],
				},

				{
					name: routesNames.TOOLS,
					path: '/tools',
					component: () => import('@/components/tools/Tools'),
					children: [
						{
							name: routesNames.FORM_BUILDER,
							path: 'form-builder',
							component: () =>
								import(
									'@/components/tools/formBuilder/FormBuilder.vue'
								),
						},
						{
							name: routesNames.EDIT_INVENTORY_FORM,
							path: 'inventory-form/edit/:id',
							component: () =>
								import(
									'@/components/tools/formBuilder/InventoryForm.vue'
								),
						},
						{
							name: routesNames.ADD_INVENTORY_FORM,
							path: 'inventory-form/add',
							component: () =>
								import(
									'@/components/tools/formBuilder/InventoryForm.vue'
								),
						},
						{
							name: routesNames.ACTIVITY_BUILDER,
							path: 'activity-builder',
							component: () =>
								import(
									'@/components/tools/activityBuilder/ActivityBuilder.vue'
								),
						},
						{
							name: routesNames.ADD_ACTIVITY,
							path: 'activity-builder/add',
							component: () =>
								import(
									'@/components/tools/activityBuilder/Activity.vue'
								),
						},
						{
							name: routesNames.EDIT_ACTIVITY,
							path: 'activity-builder/edit/:id',
							component: () =>
								import(
									'@/components/tools/activityBuilder/Activity.vue'
								),
						},
						{
							name: routesNames.DATA_IMPORT,
							path: 'data-import',
							component: () =>
								import(
									'@/components/tools/dataImport/DataImport.vue'
								),
						},
						{
							name: routesNames.DATA_IMPORT_TEMPLATES,
							path: 'data-import/templates',
							component: () =>
								import(
									'@/components/tools/dataImport/DataImportTemplates.vue'
								),
						},
						{
							name: routesNames.DATA_EXPORT,
							path: 'data-exports',
							component: () =>
								import(
									'@/components/tools/dataExport/DataExport.vue'
								),
						},
						{
							name: routesNames.USER_MANAGEMENT,
							path: 'user-management',
							component: () =>
								import(
									'@/components/tools/userManagement/UserManagement.vue'
								),
						},
						{
							name: routesNames.USER_MANAGEMENT_ADMIN_EDIT,
							path: 'user-management/admin-user-management-edit/:id',
							component: () =>
								import(
									'@/components/tools/userManagement/AdminUserManagementEdit.vue'
								),
						},
						{
							name: routesNames.USER_MANAGEMENT_ADMIN_ADD,
							path: 'user-management/admin-user-management-add',
							component: () =>
								import(
									'@/components/tools/userManagement/AdminUserManagementEdit.vue'
								),
						},
						{
							name: routesNames.USER_MANAGEMENT_CLIENT_EDIT,
							path: 'user-management/client-user-management-edit/:id',
							component: () =>
								import(
									'@/components/tools/userManagement/ClientUserManagementEdit.vue'
								),
						},
						{
							name: routesNames.USER_MANAGEMENT_CLIENT_ADD,
							path: 'user-management/client-user-management-add',
							component: () =>
								import(
									'@/components/tools/userManagement/ClientUserManagementEdit.vue'
								),
						},
						{
							name: routesNames.REPORT_BUILDER,
							path: 'report-builder',
							component: () =>
								import(
									'@/components/tools/reportBuilder/ReportBuilder.vue'
								),
						},
						{
							name: routesNames.REPORT_DETAILS,
							path: 'report-builder/report/:reportId?',
							component: () =>
								import(
									'@/components/tools/reportBuilder/reportDetails/ReportDetails.vue'
								),
						},

						{
							name: routesNames.WORKBOOK_DETAILS,
							path: 'report-builder/workbook/:workbookId?',
							component: () =>
								import(
									'@/components/tools/reportBuilder/workbook/WorkbookDetails.vue'
								),
						},
						{
							name: routesNames.PROJECT_CLONE,
							path: 'clone-project',
							component: () =>
								import(
									'@/components/tools/projectClone/ProjectClone.vue'
								),
						},
					],
				},
				{
					name: routesNames.SETTINGS,
					path: '/settings',
					component: () => import('@/components/settings/Settings'),
					children: [
						{
							name: routesNames.DATABASE_TABLES,
							path: 'data-fields',
							component: () =>
								import(
									'@/components/settings/dataFields/DataFields.vue'
								),
						},
						{
							name: routesNames.DATASET_FIELDS,
							path: 'grid-fields',
							component: () =>
								import(
									'@/components/settings/gridFields/GridFields.vue'
								),
						},
						{
							name: routesNames.PERMISSIONS,
							path: 'permissions',
							component: () =>
								import('@/components/settings/Permissions.vue'),
						},
						{
							name: routesNames.REFERENCE_LISTS,
							path: 'reference-lists',
							component: () =>
								import(
									'@/components/settings/referenceLists/ReferenceLists.vue'
								),
						},
						{
							name: routesNames.GENERAL_SETTINGS,
							path: 'general-settings',
							component: () =>
								import(
									'@/components/settings/generalSettings/GeneralSettings.vue'
								),
						},
						{
							name: routesNames.MERGE_CONDITIONS,
							path: 'merge-conditions',
							component: () =>
								import(
									'@/components/settings/mergeConditions/MergeConditions.vue'
								),
						},
						{
							name: routesNames.CONNECTORS,
							path: 'connectors',
							component: () =>
								import(
									'@/components/settings/connectors/Connectors.vue'
								),
						},
					],
				},
				{
					name: routesNames.CLIENTS,
					path: '/clients-management',
					component: () =>
						import(
							'@/components/clientsManagement/ClientsManagementList'
						),
				},
				{
					name: routesNames.CLIENTS_EDIT,
					path: '/clients-management/edit/:id',
					component: () =>
						import(
							'@/components/clientsManagement/ClientsManagementEdit'
						),
				},
				{
					name: routesNames.CLIENTS_ADD,
					path: '/clients-management/add',
					component: () =>
						import(
							'@/components/clientsManagement/ClientsManagementEdit'
						),
				},
				{
					name: routesNames.PROJECTS,
					path: '/projects-management',
					component: () =>
						import(
							'@/components/projectsManagement/ProjectsManagementList'
						),
				},
				{
					name: routesNames.PROJECTS_EDIT,
					path: '/projects-management/edit/:id',
					component: () =>
						import(
							'@/components/projectsManagement/ProjectsManagementEdit'
						),
				},
				{
					name: routesNames.PROJECTS_ADD,
					path: '/projects-management/add',
					component: () =>
						import(
							'@/components/projectsManagement/ProjectsManagementEdit'
						),
				},
			],
		},
	],
});

router.beforeResolve(async (to, from, next) => {
	if (localStorage.bearer) {
		let authenticated =
			router.app.$store.getters[AuthGetterTypes.IS_AUTHENTICATED];
		if (authenticated) {
			let profileLoaded =
				router.app.$store.getters[AuthGetterTypes.PROFILE_LOADED];
			while (!profileLoaded) {
				profileLoaded =
					router.app.$store.getters[AuthGetterTypes.PROFILE_LOADED];
				await delay(100);
			}

			let hasPermissions = permissionCheckService.hasRoutePermission(
				to.name
			);

			if (!hasPermissions) {
				next({ name: routesNames.RESTRICTED_ACCESS });
			} else {
				next();
			}
		} else {
			next();
		}
	} else {
		next();
	}
});

function delay(time) {
	return new Promise((resolve) => setTimeout(resolve, time));
}

export default router;
